import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const ConsultantTrainingHub = () => {
  const [activeSection, setActiveSection] = useState('question-guide');
  const navigate = useNavigate();

  const questionGuides = {
    'Food & Beverage': {
      count: 45,
      commonClarifications: [
        'Process heating vs. space heating distinctions',
        'Refrigeration system types and energy usage',
        'Steam system pressure levels and applications',
        'Compressed air usage in food processing',
        'Lighting requirements for food safety compliance'
      ],
      industryContext: 'Food & beverage facilities typically have high refrigeration loads and steam systems for processing. Focus on process-specific energy uses.',
      keyQuestions: [
        'Do you have steam/gas metering at the process line level?',
        'Do you use highest temperature for chilling or cold storage?',
        'Have you implemented repair and eliminate steam leaks?'
      ]
    },
    'Metal Products': {
      count: 40,
      commonClarifications: [
        'Furnace types and operating temperatures',
        'Compressed air for pneumatic tools vs. cleaning',
        'Heat treatment processes and recovery opportunities',
        'Motor loads for heavy machinery',
        'Welding and cutting equipment energy usage'
      ],
      industryContext: 'Metal products manufacturing involves high-temperature processes and heavy motor loads. Waste heat recovery is often viable.',
      keyQuestions: [
        'Have you implemented repair furnaces and oven doors so that they seal efficiently?',
        'Do you reduce combustion air flow to optimum?',
        'Have you implemented isolate hot or cold equipment?'
      ]
    }
  };

  const bestPractices = [
    {
      title: 'Video Call Setup',
      icon: '📹',
      practices: [
        'Use dual monitors - one for client video, one for assessment interface',
        'Ensure stable internet connection (minimum 5 Mbps upload)',
        'Test screen sharing before client calls',
        'Have backup communication method (phone) ready',
        'Use good lighting and professional background'
      ]
    },
    {
      title: 'Question Flow Management',
      icon: '❓',
      practices: [
        'Start with easy facility information questions to build rapport',
        'Group related questions together to maintain context',
        'Allow time for client to check information with colleagues',
        'Use the pause feature if client needs to leave temporarily',
        'Explain the purpose of technical questions before asking'
      ]
    },
    {
      title: 'Technical Troubleshooting',
      icon: '🔧',
      practices: [
        'Keep assessment page bookmarked for quick access',
        'Have client refresh browser if forms are not loading',
        'Use private/incognito mode to avoid browser cache issues',
        'Guide client through enabling screen sharing permissions',
        'Keep IT support contact readily available'
      ]
    },
    {
      title: 'Client Engagement',
      icon: '👥',
      practices: [
        'Explain how each question relates to energy savings potential',
        'Share relevant examples from similar facilities',
        'Discuss implementation challenges and solutions',
        'Encourage questions and provide detailed explanations',
        'Schedule follow-up calls during the session'
      ]
    }
  ];

  const troubleshootingGuides = [
    {
      issue: 'Assessment form not loading',
      solution: 'Ask client to refresh browser, clear cache, or try incognito mode. Verify they have JavaScript enabled.',
      prevention: 'Send system requirements email 24 hours before session'
    },
    {
      issue: 'Client cannot select industry from dropdown',
      solution: 'Have them click directly on the dropdown arrow, not the text. If still issues, you can navigate directly to /food-beverage or specific industry page.',
      prevention: 'Test the assessment flow before each session'
    },
    {
      issue: 'Question responses not saving',
      solution: 'Ensure client has stable internet. Use manual save prompts between sections. Keep backup notes.',
      prevention: 'Advise clients to use wired connection when possible'
    },
    {
      issue: 'Results page showing errors',
      solution: 'Refresh the page, check assessment ID in URL. Contact technical support if persistent.',
      prevention: 'Complete a test assessment monthly to verify system functionality'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/consultant/dashboard')}
                className="text-gray-500 hover:text-gray-700"
              >
                ← Back to Dashboard
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Training Hub</h1>
                <p className="text-gray-600">Consultant resources and best practices</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Navigation Tabs */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-8">
          <div className="px-6 py-2 border-b border-gray-200">
            <nav className="flex space-x-8">
              {[
                { id: 'question-guide', label: 'Question Guides', icon: '📋' },
                { id: 'best-practices', label: 'Best Practices', icon: '⭐' },
                { id: 'troubleshooting', label: 'Troubleshooting', icon: '🔧' },
                { id: 'video-tips', label: 'Video Call Tips', icon: '📹' }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveSection(tab.id)}
                  className={`flex items-center gap-2 py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeSection === tab.id
                      ? 'border-emerald-500 text-emerald-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <span>{tab.icon}</span>
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Content */}
          <div className="p-6">
            {/* Question Guides */}
            {activeSection === 'question-guide' && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-xl font-bold text-gray-900 mb-4">Industry-Specific Question Guides</h2>
                  <p className="text-gray-600 mb-6">
                    Detailed guidance for conducting assessments across different manufacturing sectors.
                  </p>
                </div>

                {Object.entries(questionGuides).map(([industry, guide]) => (
                  <div key={industry} className="bg-gray-50 rounded-lg p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-gray-900">{industry}</h3>
                      <span className="px-3 py-1 bg-emerald-100 text-emerald-800 text-sm font-medium rounded-full">
                        {guide.count} questions
                      </span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <h4 className="font-medium text-gray-900 mb-2">Common Clarifications Needed:</h4>
                        <ul className="space-y-1 text-sm text-gray-600">
                          {guide.commonClarifications.map((clarification, index) => (
                            <li key={index} className="flex items-start">
                              <span className="text-emerald-500 mr-2">•</span>
                              {clarification}
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div>
                        <h4 className="font-medium text-gray-900 mb-2">Key Questions:</h4>
                        <ul className="space-y-1 text-sm text-gray-600">
                          {guide.keyQuestions.map((question, index) => (
                            <li key={index} className="flex items-start">
                              <span className="text-blue-500 mr-2">▸</span>
                              {question}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>

                    <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                      <h4 className="font-medium text-blue-900 mb-1">Industry Context:</h4>
                      <p className="text-sm text-blue-800">{guide.industryContext}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Best Practices */}
            {activeSection === 'best-practices' && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-xl font-bold text-gray-900 mb-4">Best Practices for Consultant-Led Sessions</h2>
                  <p className="text-gray-600 mb-6">
                    Proven strategies for conducting effective video-based energy assessments.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {bestPractices.map((section, index) => (
                    <div key={index} className="bg-white border border-gray-200 rounded-lg p-6">
                      <div className="flex items-center mb-3">
                        <span className="text-2xl mr-3">{section.icon}</span>
                        <h3 className="text-lg font-semibold text-gray-900">{section.title}</h3>
                      </div>
                      
                      <ul className="space-y-2">
                        {section.practices.map((practice, pIndex) => (
                          <li key={pIndex} className="flex items-start">
                            <svg className="w-5 h-5 text-emerald-500 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            <span className="text-sm text-gray-700">{practice}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Troubleshooting */}
            {activeSection === 'troubleshooting' && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-xl font-bold text-gray-900 mb-4">Common Issues & Solutions</h2>
                  <p className="text-gray-600 mb-6">
                    Quick reference for resolving technical problems during client sessions.
                  </p>
                </div>

                <div className="space-y-4">
                  {troubleshootingGuides.map((guide, index) => (
                    <div key={index} className="bg-white border border-gray-200 rounded-lg p-6">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center mb-2">
                            <svg className="w-5 h-5 text-red-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <h3 className="text-lg font-medium text-gray-900">{guide.issue}</h3>
                          </div>
                          
                          <div className="mb-3">
                            <h4 className="font-medium text-green-700 mb-1">Solution:</h4>
                            <p className="text-sm text-gray-700">{guide.solution}</p>
                          </div>
                          
                          <div>
                            <h4 className="font-medium text-blue-700 mb-1">Prevention:</h4>
                            <p className="text-sm text-gray-700">{guide.prevention}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Video Call Tips */}
            {activeSection === 'video-tips' && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-xl font-bold text-gray-900 mb-4">Video Call Optimization</h2>
                  <p className="text-gray-600 mb-6">
                    Technical setup and communication strategies for seamless video assessments.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-white border border-gray-200 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Technical Setup</h3>
                    <ul className="space-y-3">
                      <li className="flex items-start">
                        <span className="font-medium text-emerald-600 mr-2">✓</span>
                        <div>
                          <div className="font-medium">Dual Monitor Setup</div>
                          <div className="text-sm text-gray-600">Client video on monitor 1, assessment interface on monitor 2</div>
                        </div>
                      </li>
                      <li className="flex items-start">
                        <span className="font-medium text-emerald-600 mr-2">✓</span>
                        <div>
                          <div className="font-medium">Internet Speed</div>
                          <div className="text-sm text-gray-600">Minimum 5 Mbps upload for smooth screen sharing</div>
                        </div>
                      </li>
                      <li className="flex items-start">
                        <span className="font-medium text-emerald-600 mr-2">✓</span>
                        <div>
                          <div className="font-medium">Backup Communication</div>
                          <div className="text-sm text-gray-600">Have client's phone number for technical issues</div>
                        </div>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-white border border-gray-200 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Communication Tips</h3>
                    <ul className="space-y-3">
                      <li className="flex items-start">
                        <span className="font-medium text-blue-600 mr-2">💡</span>
                        <div>
                          <div className="font-medium">Screen Sharing Etiquette</div>
                          <div className="text-sm text-gray-600">Announce what you're sharing and ask for confirmation</div>
                        </div>
                      </li>
                      <li className="flex items-start">
                        <span className="font-medium text-blue-600 mr-2">💡</span>
                        <div>
                          <div className="font-medium">Pace Management</div>
                          <div className="text-sm text-gray-600">Allow extra time for client to read and respond</div>
                        </div>
                      </li>
                      <li className="flex items-start">
                        <span className="font-medium text-blue-600 mr-2">💡</span>
                        <div>
                          <div className="font-medium">Engagement Checks</div>
                          <div className="text-sm text-gray-600">Ask "Can you see my screen clearly?" every 10-15 minutes</div>
                        </div>
                      </li>
                    </ul>
                  </div>
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                  <div className="flex items-center mb-3">
                    <svg className="w-6 h-6 text-yellow-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <h3 className="text-lg font-medium text-yellow-800">Platform-Specific Recommendations</h3>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <h4 className="font-medium text-yellow-800 mb-2">Microsoft Teams</h4>
                      <ul className="space-y-1 text-yellow-700">
                        <li>• Use "Share specific application" vs entire screen</li>
                        <li>• Enable "Give control" for client navigation</li>
                        <li>• Record sessions with client consent</li>
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-medium text-yellow-800 mb-2">Zoom</h4>
                      <ul className="space-y-1 text-yellow-700">
                        <li>• Use annotation tools to highlight sections</li>
                        <li>• Enable "Remote Control" for client assistance</li>
                        <li>• Use breakout rooms for multi-stakeholder sessions</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConsultantTrainingHub;